import type { TileListPageLoadResult, TileListPageSession } from '@tilelist/application/core/TileListSession.ts';
import type { SelectionUpdate } from '@tilelist/application/ports/SelectionEvents.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';

/*                                                              */
export type AcceptedPageLoad = {
    update: SelectionUpdate;
    result: TileListPageLoadResult;
    isCurrent(): boolean;
};

export type OrderedPageLoads = {
    reload(update: SelectionUpdate): Promise<AcceptedPageLoad | undefined>;
    restoreInitial(
        page: TileListPageLayout,
        restore: (page: TileListPageLayout) => Promise<TileListPageLayout>,
    ): Promise<TileListPageLayout | undefined>;
    stop(): void;
};

/*                                                                                 */
export function createOrderedPageLoads(session: Pick<TileListPageSession, 'reload'>): OrderedPageLoads {
    let generation = 0;
    let active = true;

    return {
        async reload(update: SelectionUpdate) {
            const currentGeneration = ++generation;
            const result = await session.reload(update);
            if (!active || currentGeneration !== generation) return;
            return { update, result, isCurrent: () => active && currentGeneration === generation };
        },
        async restoreInitial(
            page: TileListPageLayout,
            restore: (page: TileListPageLayout) => Promise<TileListPageLayout>,
        ) {
            const restored = await restore(page);
            if (!active || generation !== 0) return;
            return restored;
        },
        stop() {
            active = false;
        },
    };
}
